import { useConnections } from '@features/rebuild-settings/use-rebuild-settings';
import { useMemo } from 'react';
import {
  type FilterOption,
  MappingActions,
  showInactiveFilter,
} from '../MappingActions';
import { BAND_LABELS } from '../../lib/bands';
import { ANY, type SuggestionFilter } from '../../lib/suggestion-params';
import type { ConfidenceBand } from '../../lib/suggestion-types';

const BAND_OPTIONS: FilterOption[] = [
  { value: ANY, label: 'Any confidence' },
  ...(Object.keys(BAND_LABELS) as ConfidenceBand[]).map(band => ({
    value: band,
    label: BAND_LABELS[band],
  })),
];

export type MappingFiltersProps = {
  filter: SuggestionFilter;
  onChange: <K extends keyof SuggestionFilter>(
    key: K,
    value: SuggestionFilter[K],
  ) => void;
  /** Listings with no suggestion have no confidence to filter by. */
  showBand: boolean;
};

/**
 * Where the listings come from (connection), how sure the match is (band),
 * whether inactive listings show, and a search.
 */
export function MappingFilters({
  filter,
  onChange,
  showBand,
}: MappingFiltersProps) {
  const { data } = useConnections();
  const connectionOptions = useMemo<FilterOption[]>(
    () => [
      { value: ANY, label: 'All connections' },
      ...(data?.v2
        ? data.connections
            .filter(connection => connection.kind !== 'other')
            .map(connection => ({
              value: connection.id,
              label: connection.name ?? connection.provider ?? 'Connection',
            }))
        : []),
    ],
    [data],
  );

  return (
    <div className="sk:flex sk:flex-wrap sk:items-center sk:gap-3">
      <MappingActions
        filters={[
          {
            id: 'connection',
            label: 'Connection',
            value: filter.connectionId,
            options: connectionOptions,
            onChange: value => onChange('connectionId', value),
          },
          ...(showBand
            ? [
                {
                  id: 'band',
                  label: 'Confidence',
                  value: filter.band,
                  options: BAND_OPTIONS,
                  onChange: (value: string) =>
                    onChange('band', value as SuggestionFilter['band']),
                },
              ]
            : []),
          showInactiveFilter(filter.showInactive, isOn =>
            onChange('showInactive', isOn),
          ),
        ]}
        search={filter.search}
        onSearch={search => onChange('search', search)}
      />
    </div>
  );
}
